import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CloseIcon from '@mui/icons-material/Close'
import { useContext, useEffect, useMemo, useState } from 'react'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import {
  checkboxPrimarySx,
  dialogCancelButtonSx,
  dialogLoadingOverlaySx,
  dialogSaveButtonSx,
  dropdownBackdropSx,
  selectableRowSx,
  uiPalette,
  dropdownMenuSx,
  dialogActionsSx,
  dropdownSearchInputSx,
  dropdownFieldsetSx,
} from '../constants/uiSx'
import { LanguageContext } from '../util/LanguageContext'
import { ModalConfirmDialogDCP } from './ModalConfirmDialogDCP'
import { useDCPGroupGet, type GroupInfo } from '../hooks/useDCPGroupGet'
import { useCF03GroupUpdate } from '../hooks/useCF03GroupUpdate'

interface TenantDevice {
  device_id: string
  cotopat_id?: string
  memo?: string
  group_ids?: string[]
  device_type?: string
  isLicenseActive?: boolean
}

interface GroupAssignmentDialogProps {
  open: boolean
  tenant: string
  devices: TenantDevice[]
  onClose: () => void
  onSave: () => Promise<void>
}


export const GroupAssignmentDialog: React.FC<GroupAssignmentDialogProps> = ({
  open,
  tenant,
  devices,
  onClose,
  onSave,
}) => {
  const translation = useContext(LanguageContext)
  const cf03GroupUpdate = useCF03GroupUpdate()
  const dcpGroupGet = useDCPGroupGet()

  const [allGroups, setAllGroups] = useState<GroupInfo[]>([])
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [initialSelectedDeviceIds, setInitialSelectedDeviceIds] = useState<string[]>([])
  const [groupSearchKeyword, setGroupSearchKeyword] = useState('')
  const [deviceSearchKeyword, setDeviceSearchKeyword] = useState('')
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false)
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false)
  const [errorDialog, setErrorDialog] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const loading = dcpGroupGet.loading === loadStatus.LOADING

  // グループ一覧を取得（ダイアログ開時）
  useEffect(() => {
    if (!open) {
      setAllGroups([])
      setSelectedGroupId(null)
      setSelectedDeviceIds([])
      setInitialSelectedDeviceIds([])
      setGroupSearchKeyword('')
      setDeviceSearchKeyword('')
      setGroupDropdownOpen(false)
      setDeviceDropdownOpen(false)
      return
    }

    const fetchGroups = async () => {
      // 全グループを取得（ページング無しで全て取得）
      let start = 0
      let allGroupsList: GroupInfo[] = []
      let hasMore = true

      while (hasMore) {
        const result = await dcpGroupGet.execGroupGet(tenant, start, 100)
        if (result.success && result.info) {
          allGroupsList = [...allGroupsList, ...result.info.groups]
          hasMore = allGroupsList.length < result.info.count
          start = allGroupsList.length
        } else {
          setErrorDialog(result.message ?? errorMsg.SYSTEM_ERROR)
          return
        }
      }

      setAllGroups(allGroupsList)
    }

    fetchGroups()
  }, [open, tenant])

  // グループが変更されたとき、そのグループに属するデバイスを取得
  useEffect(() => {
    if (!selectedGroupId) {
      setSelectedDeviceIds([])
      setInitialSelectedDeviceIds([])
      return
    }

    // 選択されたグループに属するデバイスを抽出
    const groupDevices = devices.filter(
      d => Array.isArray(d.group_ids) && d.group_ids.includes(selectedGroupId)
    )
    const groupDeviceIds = groupDevices.map(d => d.device_id)

    setSelectedDeviceIds(groupDeviceIds)
    setInitialSelectedDeviceIds(groupDeviceIds)
  }, [selectedGroupId, devices])

  // グループ検索フィルタリング
  const filteredGroups = useMemo(() => {
    if (!groupSearchKeyword) return allGroups

    const keyword = groupSearchKeyword.toLowerCase()
    return allGroups.filter(group => group.name.toLowerCase().includes(keyword))
  }, [allGroups, groupSearchKeyword])

  // デバイス検索フィルタリング
  const filteredDevices = useMemo(() => {
    const activeDevices = devices.filter(device => device.isLicenseActive !== false)
    if (!deviceSearchKeyword) return activeDevices

    const keyword = deviceSearchKeyword.toLowerCase()
    return activeDevices.filter(
      device =>
        (device.cotopat_id?.toLowerCase().includes(keyword)) ||
        (device.memo?.toLowerCase().includes(keyword))
    )
  }, [devices, deviceSearchKeyword])

  // 選択状態の変更を検出（グループ未選択時は保存不可）
  const isSelectionChanged = useMemo(() => {
    if (!selectedGroupId) return false
    if (selectedDeviceIds.length !== initialSelectedDeviceIds.length) return true
    const initialSet = new Set(initialSelectedDeviceIds)
    return selectedDeviceIds.some(id => !initialSet.has(id))
  }, [selectedGroupId, selectedDeviceIds, initialSelectedDeviceIds])

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId)
    setGroupDropdownOpen(false)
    setGroupSearchKeyword('')
  }

  const handleToggleDevice = (deviceId: string) => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    )
  }

  const handleToggleAllDevices = () => {
    if (selectedDeviceIds.length === filteredDevices.length && filteredDevices.length > 0) {
      // すべて選択解除
      setSelectedDeviceIds(prev =>
        prev.filter(id => !filteredDevices.find(d => d.device_id === id))
      )
    } else {
      // すべて選択
      const allFilteredIds = filteredDevices.map(d => d.device_id)
      setSelectedDeviceIds(prev => {
        const set = new Set(prev)
        allFilteredIds.forEach(id => set.add(id))
        return Array.from(set)
      })
    }
  }

  const allDevicesSelected =
    filteredDevices.length > 0 &&
    filteredDevices.every(device => selectedDeviceIds.includes(device.device_id))

  const someDevicesSelected =
    filteredDevices.some(device => selectedDeviceIds.includes(device.device_id)) &&
    !allDevicesSelected

  const isDeviceSelectDisabled = !selectedGroupId

  const handleSave = async () => {
    if (!selectedGroupId) {
      setErrorDialog(translation['label_invalid_parameter'] ?? errorMsg.SYSTEM_ERROR)
      return
    }

    try {
      setIsSaving(true)

      // 変更があったグループのデバイスリストを更新
      // 新しい選択デバイスで、選択されたグループを更新
      const result = await cf03GroupUpdate.execGroupUpdate(tenant, selectedGroupId, selectedDeviceIds)
      if (!result.success) {
        setErrorDialog(result.message ?? errorMsg.SYSTEM_ERROR)
        return
      }

      if ((result.info?.not_found_device_ids?.length ?? 0) > 0) {
        const missingCotopatIds = result.info!.not_found_device_ids.map(notFoundDeviceId => {
          const device = devices.find(({ device_id }) => device_id === notFoundDeviceId)
          return device?.cotopat_id ?? notFoundDeviceId
        })

        setErrorDialog(
          `${translation['label_not_found_target_device'] ?? errorMsg.SYSTEM_ERROR} ${translation['label_cotopat_id']}: ${missingCotopatIds.join(', ')}`,
        )
        return
      }

      // 保存成功後、呼び出し元で refresh を実行させる
      await onSave()
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  const selectedGroup = allGroups.find(g => g.id === selectedGroupId)

  return (
    <>
      {errorDialog && (
        <ModalConfirmDialogDCP
          open={!!errorDialog}
          text={errorDialog}
          onClick={() => setErrorDialog(null)}
          buttonText='button_close'
        />
      )}

      <Dialog
        open={open}
        onClose={(event, reason) => {
          // ドロップダウンが開いている場合、backdropクリックでDialog closeをしない
          if (deviceDropdownOpen || groupDropdownOpen) {
            if (reason === 'backdropClick') {
              return
            }
          }
          onClose()
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ mt: 2, fontSize: '1.5rem', fontWeight: 700, textAlign: 'center' }}>
          {translation['label_view_permission_setting']}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 4 }, height: 600, position: 'relative' }}>
          <Typography sx={{ mt: 1, mb: 3, fontSize: '1rem', textAlign: 'center', color: '#666' }}>
            {translation['message_group_assignment']}
          </Typography>

          <>
            {/* グループを選択 */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                {translation['label_select_group']}
              </Typography>

              <Box sx={{ position: 'relative', width: '60%' }}>
                <OutlinedInput
                  fullWidth
                  value={groupDropdownOpen ? groupSearchKeyword : selectedGroup?.name || ''}
                  onChange={(e) => setGroupSearchKeyword(e.target.value)}
                  onFocus={() => {
                    setGroupDropdownOpen(true)
                    setGroupSearchKeyword('')
                  }}
                  placeholder={(!selectedGroup || groupDropdownOpen) ? translation['search'] : undefined}
                  endAdornment={
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(0,0,0,0.54)', pointerEvents: 'none' }}>
                      {groupDropdownOpen
                        ? <KeyboardArrowUpIcon fontSize='small' />
                        : <KeyboardArrowDownIcon fontSize='small' />}
                    </Box>
                  }
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-input': {
                      py: '8px',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#9E9E9E',
                      opacity: 1,
                    },
                    borderColor: groupDropdownOpen ? '#0A9BCD' : undefined,
                    borderWidth: groupDropdownOpen ? '2px' : undefined,
                  }}
                />

                {groupDropdownOpen && (
                  <Box
                    sx={dropdownMenuSx(250)}
                  >
                    {filteredGroups.map(group => (
                      <Box
                        key={group.id}
                        onClick={() => handleGroupSelect(group.id)}
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          backgroundColor:
                            selectedGroupId === group.id ? uiPalette.selectedBg : 'transparent',
                          '&:hover': {
                            backgroundColor:
                              selectedGroupId === group.id ? uiPalette.selectedBg : uiPalette.hoverBg,
                          },
                        }}
                      >
                        {group.name}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {groupDropdownOpen && (
                <Box
                  sx={dropdownBackdropSx}
                  onClick={(e) => {
                    if (e.currentTarget === e.target) {
                      setGroupDropdownOpen(false)
                    }
                  }}
                />
              )}
            </Box>

            {/* デバイスを選択 */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                {translation['label_select_device']}
              </Typography>

              {/* ドロップダウン表示中もバックドロップに隠れないよう前面に出す */}
              <Box sx={{ position: 'relative', zIndex: deviceDropdownOpen ? 1300 : undefined }}>
                <Box
                  component='fieldset'
                  sx={{
                    ...dropdownFieldsetSx(deviceDropdownOpen),
                    ...(isDeviceSelectDisabled && {
                      backgroundColor: '#F0F0F0',
                      borderColor: '#D9D9D9',
                      cursor: 'default',
                      '& .MuiInput-input::placeholder': {
                        color: uiPalette.disabledText,
                      },
                    }),
                  }}
                  onClick={() => {
                    if (isDeviceSelectDisabled) return
                    setDeviceDropdownOpen(true)
                  }}
                >
                  {selectedDeviceIds.length > 0 && (
                    <>
                      {devices
                        .filter(d => selectedDeviceIds.includes(d.device_id))
                        .map(device => (
                          <Chip
                            key={device.device_id}
                            label={device.cotopat_id || device.device_id}
                            onDelete={(e) => {
                              e.stopPropagation()
                              handleToggleDevice(device.device_id)
                            }}
                            size="small"
                            sx={{
                              height: 'auto',
                              '& .MuiChip-label': {
                                px: 1,
                                py: 0.5,
                              },
                            }}
                          />
                        ))}
                    </>
                  )}

                  {/* 検索テキスト入力 */}
                  <TextField
                    value={deviceSearchKeyword}
                    onChange={(e) => setDeviceSearchKeyword(e.target.value)}
                    onFocus={() => setDeviceDropdownOpen(true)}
                    disabled={isDeviceSelectDisabled}
                    placeholder={allDevicesSelected ? undefined : translation['label_new_device']}
                    variant="standard"
                    size="small"
                    InputProps={{ disableUnderline: true }}
                    sx={dropdownSearchInputSx(deviceDropdownOpen)}
                  />

                  {/* 右側アイコン（全解除 + ドロップダウン） */}
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', color: 'rgba(0,0,0,0.54)' }}>
                    {selectedDeviceIds.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDeviceIds([])
                        }}
                        sx={{ mr: 0.25 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {deviceDropdownOpen
                      ? <KeyboardArrowUpIcon fontSize='small' />
                      : <KeyboardArrowDownIcon fontSize='small' />}
                  </Box>
                </Box>

                {/* ドロップダウンテーブル */}
                {deviceDropdownOpen && (
                  <Box
                    sx={dropdownMenuSx(360)}
                  >
                    <TableContainer>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 50, p: 1 }}>
                              <Checkbox
                                checked={allDevicesSelected}
                                indeterminate={someDevicesSelected}
                                onChange={handleToggleAllDevices}
                                size="small"
                                sx={checkboxPrimarySx}
                              />
                            </TableCell>
                            <TableCell sx={{ p: 1, fontSize: '0.875rem', fontWeight: 600, width: 100 }}>
                              {translation['label_device_type']}
                            </TableCell>
                            <TableCell sx={{ p: 1, fontSize: '0.875rem', fontWeight: 600, width: 100 }}>
                              {translation['label_cotopat_id']}
                            </TableCell>
                            <TableCell sx={{ p: 1, fontSize: '0.875rem', fontWeight: 600 }}>
                              {translation['label_memo_input']}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredDevices.map(device => (
                            <TableRow
                              key={device.device_id}
                              sx={selectableRowSx(selectedDeviceIds.includes(device.device_id))}
                              onClick={() => handleToggleDevice(device.device_id)}
                            >
                              <TableCell sx={{ p: 1 }}>
                                <Checkbox
                                  checked={selectedDeviceIds.includes(device.device_id)}
                                  onChange={() => handleToggleDevice(device.device_id)}
                                  onClick={(e) => e.stopPropagation()}
                                  size="small"
                                  sx={checkboxPrimarySx}
                                />
                              </TableCell>
                              <TableCell sx={{ p: 1, fontSize: '0.875rem' }}>
                                {device.device_type || ''}
                              </TableCell>
                              <TableCell sx={{ p: 1, fontSize: '0.875rem' }}>
                                {device.cotopat_id || ''}
                              </TableCell>
                              <TableCell sx={{ p: 1, fontSize: '0.875rem' }}>
                                {device.memo || ''}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>

              {deviceDropdownOpen && (
                <Box
                  sx={dropdownBackdropSx}
                  onClick={(e) => {
                    if (e.currentTarget === e.target) {
                      setDeviceDropdownOpen(false)
                    }
                  }}
                />
              )}
            </Box>
          </>

          {/* ローディングオーバーレイ（初期ロード時と保存中） */}
          {(loading || isSaving) && (
            <Box
              sx={dialogLoadingOverlaySx}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx()}>
          <Button
            onClick={onClose}
            sx={dialogCancelButtonSx}
            disabled={isSaving || loading}
          >
            {translation['button_cancel']}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isSelectionChanged || isSaving || loading}
            sx={dialogSaveButtonSx(isSelectionChanged && !isSaving && !loading)}
          >
            {translation['button_save']}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
